import { useState } from 'react';
import { TextField, makeStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import moment from 'moment';

import { OrganizationDeliveryPoint } from '../../../organizations/types/organization.type';
import { stringDateToDeliveryDay } from '../../utils/format-delivery-schedule.util';
import { DeliveryDay } from '../../types/delivery-selection.type';

type Props = {
  deliveryPoints: OrganizationDeliveryPoint[];
  selectedDeliveryPoint: OrganizationDeliveryPoint | null;
  selectedDay: DeliveryDay | null;
  onDeliveryPointChange: (deliveryPoint: OrganizationDeliveryPoint | null) => void;
  onDayChange: (day: DeliveryDay | null) => void;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  dateSchedule: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem',
    alignItems: 'center',
  },
  scheduleInfo: {
    margin: 0,
    opacity: 0.7,
    fontSize: '0.85rem',
  },
});

function toScheduleTime(time: string): string {
  return time.replace(':', 'H');
}

function buildSchedule(start: string, end: string): string {
  return `${toScheduleTime(start)} - ${toScheduleTime(end)}`;
}

const DEFAULT_START = '09:00';
const DEFAULT_END = '17:30';

export function OrganizationDeliveryPointSection({
  deliveryPoints,
  selectedDeliveryPoint,
  selectedDay,
  onDeliveryPointChange,
  onDayChange,
}: Props) {
  const classes = useStyles();
  const [startTime, setStartTime] = useState(DEFAULT_START);
  const [endTime, setEndTime] = useState(DEFAULT_END);

  const dateValue = selectedDay?.date.format('YYYY-MM-DD') ?? moment().format('YYYY-MM-DD');

  const handleDeliveryPointChange = (deliveryPoint: OrganizationDeliveryPoint | null) => {
    onDeliveryPointChange(deliveryPoint);
    if (!deliveryPoint) {
      onDayChange(null);
      return;
    }
    onDayChange(stringDateToDeliveryDay(moment().format('YYYY-MM-DD'), buildSchedule(startTime, endTime)));
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) return;
    onDayChange(stringDateToDeliveryDay(moment(event.target.value, 'YYYY-MM-DD'), buildSchedule(startTime, endTime)));
  };

  const handleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) return;
    setStartTime(event.target.value);
    onDayChange(stringDateToDeliveryDay(dateValue, buildSchedule(event.target.value, endTime)));
  };

  const handleEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) return;
    setEndTime(event.target.value);
    onDayChange(stringDateToDeliveryDay(dateValue, buildSchedule(startTime, event.target.value)));
  };

  return (
    <div className={classes.root}>
      <Autocomplete
        value={selectedDeliveryPoint}
        onChange={(_, newValue) => handleDeliveryPointChange(typeof newValue === 'string' ? null : newValue)}
        options={[...deliveryPoints].sort((firstPoint, secondPoint) => firstPoint.name.localeCompare(secondPoint.name))}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : `${option.name} (${option.address.city} - ${option.address.zipCode})`
        }
        getOptionSelected={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label="Points de livraison" variant="outlined" />}
      />
      {selectedDeliveryPoint && (
        <>
          <div className={classes.dateSchedule}>
            <TextField
              label="Date de livraison"
              type="date"
              variant="outlined"
              value={dateValue}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Entre"
              type="time"
              variant="outlined"
              value={startTime}
              onChange={handleStartTimeChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Et"
              type="time"
              variant="outlined"
              value={endTime}
              onChange={handleEndTimeChange}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </>
      )}
    </div>
  );
}
